import { useEffect, useSyncExternalStore } from 'react';

import type { AdUnitContent } from '../contracts';

/**
 * One surface's hold, present only while its drawn unit's minimum runs, and
 * claimed only by the placement. An Evidence host makes one with
 * `useState(createAdContentHold)` and gives it to its placement and, as the
 * reveal gate, to the agents package; the note-gen placement keeps its own.
 */
export interface AdContentHold {
  isHeld: () => boolean;
  subscribe: (onChange: () => void) => () => void;
  /** Ends the hold now, as when the clinician stops the answer. */
  release: () => void;
  claim: (ms: number) => void;
}

export const createAdContentHold = (): AdContentHold => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const listeners = new Set<() => void>();
  const notify = (): void => {
    listeners.forEach((listener) => listener());
  };
  return {
    isHeld: () => timer !== undefined,
    subscribe: (onChange) => {
      listeners.add(onChange);
      return () => {
        listeners.delete(onChange);
      };
    },
    release: () => {
      if (timer === undefined) return;
      clearTimeout(timer);
      timer = undefined;
      notify();
    },
    claim: (ms) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        timer = undefined;
        notify();
      }, ms);
      notify();
    },
  };
};

/**
 * Holds for the served minimum from when a unit is drawn until it leaves. `drawnUnit` is the
 * unit on screen, not the one fetched: one the SDK declined or hid in a narrow panel is none.
 */
export function useRecordAdOnScreen({
  hold,
  minDisplayMs,
  drawnUnit,
}: {
  hold: AdContentHold | undefined;
  minDisplayMs: number;
  drawnUnit: AdUnitContent | null;
}): void {
  useEffect(() => {
    if (!hold || minDisplayMs <= 0 || !drawnUnit) return undefined;
    hold.claim(minDisplayMs);
    return () => hold.release();
  }, [hold, minDisplayMs, drawnUnit]);
}

/**
 * Pass `adVisible` to the placement (with the same `hold`) and hide the
 * content while `contentHeld`. `answerStarted` must stay false after a failure,
 * so that drops the ad at once; a stop calls `hold.release()`.
 */
export function useAdContentHold({
  hold,
  awaiting,
  answerStarted,
}: {
  hold: AdContentHold;
  awaiting: boolean;
  answerStarted: boolean;
}): { adVisible: boolean; contentHeld: boolean } {
  const held = useSyncExternalStore(hold.subscribe, hold.isHeld, () => false);
  const contentHeld = held && answerStarted && !awaiting;
  return { adVisible: awaiting || contentHeld, contentHeld };
}
